import { Component, computed, inject, input, output } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Referral, ReferralStatus } from '@xmedical/core';

@Component({
  selector: 'xm-incoming-referral-card',
  standalone: true,
  imports: [
    MatIconModule,
    MatChipsModule,
    MatTooltipModule
],
  providers: [DatePipe],
  templateUrl: './incoming-referral-card.component.html',
  styleUrl: './incoming-referral-card.component.scss'
})
export class IncomingReferralCardComponent {
  private datePipe = inject(DatePipe);

  // Inputs
  referral = input.required<Referral>();

  // Outputs
  cardClick = output<Referral>();

  // Expose ReferralStatus enum to template
  readonly ReferralStatus = ReferralStatus;

  patientSnapshot = computed(() => {
    return this.referral()?.referringPatientSnapshot || null;
  });

  patient = computed(() => {
    return this.referral()?.referringPatientSnapshot.patient || null;
  });

  // Computed: Patient initials (e.g., "TK" for "Thembisile Khanyi")
  patientInitials = computed(() => {
    const patient = this.patient();
    if (!patient) return '?';

    const first = patient.firstName?.charAt(0).toUpperCase() || '';
    const last = patient.surname?.charAt(0).toUpperCase() || '';
    return first + last || '?';
  });

  // Computed: Patient display name
  patientName = computed(() => {
    const patient = this.patient();
    if (!patient) {
      return `Patient #${patient.id}`;
    }
    return `${patient.firstName || ''} ${patient.surname || ''}`.trim() ||
           `Patient #${patient.id}`;
  });

  // Computed: Patient age
  patientAge = computed(() => {
    const dob = this.patient()?.dateOfBirth;
    if (!dob) return null;

    const dobDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }
    return `${age}y`;
  });

  // Computed: Patient gender
  patientGender = computed(() => {
    const gender = this.patient()?.gender;
    if (!gender) return null;
    return gender;
  });

  // Computed: Sending practitioner name
  referringPractitionerName = computed(() => {
    return this.referral().referringPractitionerName || 'Unknown Practitioner';
  });

  // Computed: Clinical presence indicators (boolean only)
  hasAllergies = computed(() => {
    return (this.patientSnapshot()?.allergies?.length ?? 0) > 0;
  });

  hasMedications = computed(() => {
    return (this.patientSnapshot()?.medication?.length ?? 0) > 0;
  });

  hasProblems = computed(() => {
    return (this.patientSnapshot()?.problemList?.length ?? 0) > 0;
  });

  // Computed: Status label
  statusLabel = computed(() => {
    const status = this.referral().status;
    switch (status) {
      case ReferralStatus.Pending:
        return 'Pending';
      case ReferralStatus.Accepted:
        return 'Accepted';
      case ReferralStatus.Rejected:
        return 'Rejected';
      default:
        return 'Unknown';
    }
  });

  // Computed: Is stale
  isStale = computed(() => {
    return this.referral().overdue;
  });

  formatDate(date: Date | string): string {
    if (!date) return '';
    return this.datePipe.transform(date, 'dd MMM yyyy, HH:mm') || '';
  }

  onCardClick(): void {
    this.cardClick.emit(this.referral());
  }
}
